/**
 * Partner finder path utilities hook.
 *
 * Provides route path helpers for partner finder navigation. Automatically
 * detects the base path (/partners or /partner-finder) and returns consistent
 * paths for all partner finder routes.
 *
 * @module usePartnerFinderPath
 */

import { useLocation } from 'react-router-dom';
import { ROUTES } from '@/lib/routeConstants';

/**
 * Hook to get consistent route paths for partner finder navigation.
 *
 * Automatically detects the base path based on current location and returns
 * consistent paths for all partner finder routes. Supports both `/partners`
 * and `/partner-finder` base paths for backward compatibility.
 *
 * @returns Object containing route paths:
 *   - `basePath`: Base path for partner finder routes
 *   - `find`: Path to find partners page
 *   - `request`: Path to create partner request page
 *   - `requestEdit`: Path to edit partner request page
 *   - `messages`: Path to messages page
 *
 * @example
 * ```typescript
 * const paths = usePartnerFinderPath();
 * navigate(paths.messages); // Navigate to messages
 * ```
 */
export function usePartnerFinderPath() {
  const location = useLocation();
  const basePath = location.pathname.startsWith(ROUTES.PARTNERS) ? ROUTES.PARTNERS : '/partner-finder';
  
  return {
    basePath,
    find: basePath,
    request: `${basePath}/request`,
    requestEdit: `${basePath}/request/edit`,
    messages: `${basePath}/messages`,
  };
}

